import { useCallback, useEffect, useState } from "react";
import { api, apiConfigured, type ServerDirectory } from "../lib/api";
import type { ApiState } from "./usePlatformStatus";

/**
 * The workspace's server list, fetched from the control API.
 *
 * Deliberately not cached in `localStorage`. The previous Servers page derived
 * "am I connected?" from a locally stored guild name, which meant the answer
 * depended on which browser the customer happened to be using and survived
 * events — a removed bot, a server claimed by another workspace — that should
 * have changed it. The server is the only thing that knows, so it is asked
 * every time the page mounts, and again whenever the customer asks it to be.
 */
export function useServerDirectory(tenantId: string | null): {
  state: ApiState<ServerDirectory>;
  refresh: () => void;
} {
  const [state, setState] = useState<ApiState<ServerDirectory>>(
    apiConfigured ? { kind: "loading" } : { kind: "not-configured" },
  );
  const [nonce, setNonce] = useState(0);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!apiConfigured) {
      setState({ kind: "not-configured" });
      return;
    }
    // No workspace chosen yet. This is not a failure and must not be rendered as
    // one — the page prompts for a workspace instead.
    if (!tenantId) {
      setState({ kind: "ready", data: { servers: [], sources: [] } });
      return;
    }
    let active = true;
    setState({ kind: "loading" });
    api
      .listServers(tenantId)
      .then((data) => {
        if (active) setState({ kind: "ready", data });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setState({
          kind: "unavailable",
          ...(error instanceof Error ? { detail: error.message } : {}),
        });
      });
    return () => {
      active = false;
    };
  }, [tenantId, nonce]);

  return { state, refresh };
}
